import { getNestedValue } from './get-nested-value';

/**
 * Replaces dynamic placeholders in an object with their corresponding values.
 * @template T
 * @param {T} data - The object containing placeholders to be replaced.
 * @returns {T} A new object with placeholders replaced by their actual values.
 */
export const replaceDynamicData = <T extends object>(data: T): T => {
  const dataJson = JSON.stringify(data);

  // Updated regex to remove the customtag pattern
  const regex = /@\[.*?\]\((.*?)\)|<tag data-field-id=\\"(.*?)\\"><\/tag>/g;

  const replacedJson = dataJson.replace(regex, (_, path1, path2) => {
    const path = path1 || path2;
    const value = getNestedValue(data, path) || getNestedValue(data, `custom.${path}`);

    if (value !== undefined) {
      if (typeof value === 'string') {
        return JSON.stringify(value).slice(1, -1); // Remove surrounding quotes
      }

      // handle asset objects
      if (isAssetObject(value)) {
        return JSON.stringify({
          id: value.id,
          linkType: 'Asset',
          type: 'DynamicLink',
        });
      }

      // handle select arrays
      if (Array.isArray(value) && value.some((item) => typeof item === 'object' && item.label)) {
        return value.map((item: any) => item.label).join(', ');
      }

      return JSON.stringify(value);
    }

    return ''; // Return empty string for undefined values
  });

  try {
    const finalReplacedJson = JSON.parse(replacedJson, (key, value) => {
      if (isAssetObject(value)) {
        const imageValue = getNestedValue(data, value.id) || getNestedValue(data, `custom.${value.id}`);

        if (Array.isArray(imageValue) && imageValue.length > 1) {
          return imageValue;
        }

        return imageValue?.[0] || value;
      }
      return value;
    });

    return finalReplacedJson as T;
  } catch (error) {
    console.error('Error parsing JSON:', error);
    console.error('Replaced JSON string:', replacedJson);
    throw error;
  }
};

/**
 * Checks if a value is an Asset object.
 * @param {any} value - The value to check.
 * @returns {boolean} True if the value is an Asset object, false otherwise.
 */
function isAssetObject(value: any): boolean {
  return typeof value === 'object' && value !== null && value.linkType === 'Asset' && value.type === 'DynamicLink';
}
